import React, { createContext, useContext, useState, useEffect } from 'react';
import { esquecerSaldo } from '../../components/perfil/creditos/SaldoCreditos';

const API_URL = process.env.REACT_APP_API;
const userFunction = process.env.REACT_APP_USER_FUNCTION;
const prefix = 'api/usuario-editora';
const posfix = '-usuario-editora';
const main = process.env.REACT_APP_MAIN;
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token' + userFunction);
    const userStored = localStorage.getItem('userId' + userFunction);

    if (token && userStored) {
      setUser(userStored);
    }

    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(API_URL + prefix + '/login' + posfix, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 401) {
        throw new Error('Email e/ou senha estão incorretos.');
      } else if (!response.ok) {
        throw new Error('Falha na autenticação');
      }

      const data = await response.json();

      localStorage.setItem('token' + userFunction, data.access_token);
      localStorage.setItem('userId' + userFunction, data.user.id);

      setUser(data.user.id);
    } catch (error) {
      throw new Error('Erro durante o login: ' + error.message);
    }
  };

  // "Continuar com o Google": manda o ID token do Google para o backend, que
  // valida com o Google e devolve o NOSSO JWT. Se já existir conta com aquele
  // e-mail, entra nela (mesmos livros e pedidos) em vez de criar outra.
  const loginComGoogle = async (credential) => {
    const response = await fetch(API_URL + prefix + '/google-login' + posfix, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || 'Não foi possível entrar com o Google.');
    }

    localStorage.setItem('token' + userFunction, data.access_token);
    localStorage.setItem('userId' + userFunction, data.user.id);
    setUser(data.user.id);

    return data;
  };

  const logout = async (navigate) => {
    try {
      localStorage.removeItem('userId' + userFunction);
      localStorage.removeItem('token' + userFunction);
      // O saldo fica guardado em memória por um minuto para os dois cabeçalhos
      // dividirem a mesma consulta (ver SaldoCreditos). Sair da conta tem de
      // esquecê-lo: no computador de casa, quem entra depois veria o número de
      // quem saiu antes.
      esquecerSaldo();
      setUser(null);
      navigate(main + 'login');
    } catch (error) {
      throw new Error('Erro durante o logout: ' + error.message);
    }
  };

  const validateToken = async () => {
    try {
      const token = localStorage.getItem('token' + userFunction);

      const response = await fetch(API_URL + prefix + '/me' + posfix, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });

      if (!response.ok) {
        throw new Error('Token inválido');
      }
    } catch (error) {
      throw new Error('Erro na validação do token: ' + error.message);
    }
  };

  const signup = async (formData) => {
    try {
      const response = await fetch(API_URL + prefix + '/register' + posfix, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Falha ao criar conta');
      }

      const data = await response.json();

      localStorage.setItem('token' + userFunction, data.access_token);
      localStorage.setItem('userId' + userFunction, data.user.id);

      setUser(data.user.id);

      return data.user.id;
    } catch (error) {
      throw new Error('Erro durante a criação da conta: ' + error.message);
    }
  };

  // Confirma o e-mail com o código recebido (usa o token do usuário recém-criado)
  const verificarEmail = async (codigo) => {
    const token = localStorage.getItem('token' + userFunction);
    const response = await fetch(API_URL + prefix + '/verificar-email' + posfix, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ codigo }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Não foi possível verificar o código.');
    }
    return data;
  };

  // Reenvia um novo código de verificação por e-mail
  const reenviarCodigo = async () => {
    const token = localStorage.getItem('token' + userFunction);
    const response = await fetch(API_URL + prefix + '/reenviar-codigo' + posfix, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Não foi possível reenviar o código.');
    }
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, login, loginComGoogle, logout, validateToken, signup, verificarEmail, reenviarCodigo, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Export AuthContext for direct usage
export { AuthContext };
